import React, { useState, useCallback, useEffect } from "react";
import { ConversionForm } from './ConversionForm';
import { ConversionResult } from './ConversionResult';
import { LoadingSpinner } from "../../common/LoadingSpinner";
import { FilePreview } from './FilePreview';
import { ClipboardPreview } from "./ClipboardPreview";
import { conversionApi, ClipboardOutputRequest, FileUploadResponse } from "../../../api";
import { ConversionRequest, ConversionResponse } from "../../../types/conversionTypes";

/**
 * NCファイル変換コンポーネント
 * NCファイルの変換機能とプレビュー表示を提供する
 */
export const NCConverter: React.FC = () => {
  //#region 状態定義
  /** ファイル処理の進行状態 */
  const [isLoading, setIsLoading] = useState(false);

  /** 変換処理の結果 */
  const [result, setResult] = useState<ConversionResponse | null>(null);

  /** 入力ファイル */
  const [inputFile, setInputFile] = useState<File | null>(null);

  /** 出力ファイル */
  const [outputFile, setOutputFile] = useState<File | null>(null);

  /** 処理の進捗メッセージ */
  const [progress, setProgress] = useState<string>("");

  /** プレビュー用の画像データ */
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  /** プレビューのエラーメッセージ */
  const [previewError, setPreviewError] = useState<string | undefined>(undefined);

  /** アップロードされたファイルパス */
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null);

  /** アップロード結果 */
  const [uploadResponse, setUploadResponse] = useState<FileUploadResponse | null>(null);
  //#endregion

  //#region MIMEタイプ取得
  /**
   * ファイル拡張子に対応するMIMEタイプを取得する
   * @param extension - ファイルの拡張子
   * @returns 対応するMIMEタイプ文字列
   */
  const getMimeType = useCallback((extension: string): string => {
    const mimeTypes: { [key: string]: string } = {
      dxf: "application/dxf",
      csv: "text/csv",
      nc: "text/plain",
      txt: "text/plain",
      png: "image/png",
    };
    return mimeTypes[extension.toLowerCase()] || "text/plain";
  }, []);
  //#endregion

  //#region 状態リセット
  /**
   * アプリケーションの状態を初期化する
   * すべての状態変数をクリアし、初期状態に戻す
   */
  const resetAllStates = useCallback(() => {
    setResult(null);
    setOutputFile(null);
    setInputFile(null);
    setProgress("");
    setPreviewImage(null);
    setPreviewError(undefined);
    setUploadedFilePath(null);
    setUploadResponse(null);
  }, []);
  //#endregion

  //#region 一時ファイル削除
  /**
   * 一時ファイルの削除を実行する
   * @param filePath - 削除対象のファイルパス
   */
  const deleteTempFile = useCallback(async (filePath: string) => {
    if (!filePath) return;

    try {
      await conversionApi.deleteFile(filePath);
      console.info("一時ファイルを削除しました:", filePath);
    } catch (error) {
      console.error("ファイル削除エラー:", error);
    }
  }, []);
  //#endregion

  //#region プレビュー生成
  /**
   * NCファイルのプレビューを生成する
   * @param filePath - プレビュー対象のファイルパス
   */
  const generatePreview = useCallback(async (filePath: string) => {
    if (!filePath) return;

    try {
      setPreviewImage(null);
      setPreviewError(undefined);
      setProgress("プレビューを生成中...");

      const request: ClipboardOutputRequest = {
        inputFilePath: filePath,
        mdlType: "FUNAC",
        machineToolType: "ミル",
        contourLineDisplayMode: true,
      };

      const clipboardResult = await conversionApi.outputToClipboard(request);

      if (clipboardResult.success && clipboardResult.base64ImageData) {
        setPreviewImage(clipboardResult.base64ImageData);
      } else {
        setPreviewError(clipboardResult.message || "プレビューの生成に失敗しました");
      }
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : "プレビュー生成中にエラーが発生しました";
      console.error("プレビュー生成エラー:", error);
      setPreviewError(errorMessage);
    } finally {
      setProgress("");
    }
  }, []);
  //#endregion

  //#region ファイルアップロード
  /**
   * ファイルをサーバーにアップロードする
   * @param file - アップロードするファイル
   * @returns アップロード結果
   */
  const uploadFile = useCallback(async (file: File): Promise<FileUploadResponse> => {
    setProgress("ファイルをアップロード中...");

    try {
      const result = await conversionApi.uploadFile(file);
      
      // レスポンスを保存
      setUploadResponse(result);

      // ファイルパスが存在する場合は設定
      if (result.filePath) {
        setUploadedFilePath(result.filePath);
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : "ファイルアップロード中にエラーが発生しました";
      console.error("ファイルアップロードエラー:", error);
      throw new Error(errorMessage);
    } finally {
      setProgress("");
    }
  }, []);
  //#endregion

  //#region ファイル選択処理
  /**
   * ファイル選択時の処理を行う
   * 選択されたファイルの処理とプレビュー生成を実行
   * @param file - 選択されたファイル
   */
  const handleFileSelect = useCallback(
    async (file: File | null) => {
      resetAllStates();
      setInputFile(file);

      if (file) {
        try {
          setIsLoading(true);
          setProgress("ファイルをアップロード中...");
          const uploadResult = await conversionApi.uploadFile(file);

          console.log('uploadResult.success:', uploadResult); // uploadResultの内容確認

          if (uploadResult.success) {
            await generatePreview(uploadResult.filePath);
            await deleteTempFile(uploadResult.filePath);
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "ファイル処理中にエラーが発生しました";
          console.error("ファイル処理エラー:", error);
          setPreviewError(errorMessage);
        } finally {
          setIsLoading(false);
          setProgress("");
        }
      }
    },
    [resetAllStates, generatePreview, deleteTempFile]
  );
  //#endregion

  //#region フォームリセット
  /**
   * フォームの状態をリセットする
   * すべての状態を初期化し、ファイル入力もクリア
   */
  const handleReset = useCallback(() => {
    resetAllStates();
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  }, [resetAllStates]);
  //#endregion

  //#region ファイル変換
  /**
   * ファイル変換を実行する
   * アップロード、変換、ダウンロードの一連の処理を管理
   * @param request - 変換リクエストパラメータ
   */
  const handleConversion = useCallback(async (request: ConversionRequest) => {
    setIsLoading(true);
    setResult(null);
    setOutputFile(null);

    try {
      let targetFilePath = uploadedFilePath;
      if (!targetFilePath && inputFile) {
        const uploadResult = await uploadFile(inputFile);
        if (uploadResult.filePath) {
          targetFilePath = uploadResult.filePath;
        }
      }

      if (!targetFilePath) {
        throw new Error("ファイルが選択されていません");
      }

      setProgress("ファイルを変換中...");
      const conversionResult = await conversionApi.convertFile({
        ...request,
        inputFilePath: targetFilePath,
      });

      if (conversionResult.success && conversionResult.convertedFilePath) {
        setProgress("変換完了。ファイルをダウンロード中...");
        const fileBlob = await conversionApi.downloadFile(conversionResult.convertedFilePath);
        const fileName = conversionResult.convertedFilePath.split("\\").pop() || "output.txt";

        const file = new File([fileBlob], fileName, {
          type: getMimeType(fileName.split(".").pop() || ""),
        });
        setOutputFile(file);
        setResult(conversionResult);

        // ダウンロードリンクの生成と実行
        const downloadUrl = URL.createObjectURL(fileBlob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

        // 一時ファイルの削除
        await Promise.all([
          targetFilePath && deleteTempFile(targetFilePath),
          conversionResult.convertedFilePath && 
            deleteTempFile(conversionResult.convertedFilePath),
        ]);

        setProgress("処理が完了しました");
      } else {
        setResult(conversionResult);
        setProgress(conversionResult.message || "変換に失敗しました");
      }
    } catch (error) {
      console.error("変換処理エラー:", error);
      setResult({
        success: false,
        message: "変換処理中にエラーが発生しました",
        convertedFilePath: null,
        errorDetails: error instanceof Error ? error.message : "不明なエラー",
        clipboardOutputSuccess: false,
        clipboardOutputError: undefined,
        clipboardImagePath: null,
      });
      setProgress("エラーが発生しました");

      if (uploadedFilePath) {
        await deleteTempFile(uploadedFilePath);
      }
    } finally {
      setIsLoading(false);
    }
  }, [inputFile, uploadedFilePath, getMimeType, deleteTempFile, uploadFile]);
  //#endregion

  //#region クリーンアップ処理
  /**
   * コンポーネントのクリーンアップ時に一時ファイルを削除
   */
  useEffect(() => {
    return () => {
      if (uploadedFilePath) {
        deleteTempFile(uploadedFilePath);
      }
    };
  }, [uploadedFilePath, deleteTempFile]);
  //#endregion

  //#region UIレンダリング
  return (
    <div className="min-h-screen bg-gray-100">
      {/* メインコンテナ */}
      <div className="max-w-[1400px] mx-auto">
        {/* メインコンテンツ */}
        <div className="grid grid-cols-12 gap-6">
          {/* 左サイド: フォームと結果表示 */}
          <div className="col-span-12 lg:col-span-4">
            <div className="space-y-4">
              <div className="mx-auto max-w-md">
                <ConversionForm
                  onSubmit={handleConversion}
                  onFileSelect={handleFileSelect}
                  onReset={handleReset}
                  isLoading={isLoading}
                />
              </div>
              {result && (
                <div className="mx-auto max-w-md">
                  <ConversionResult result={result} />
                </div>
              )}
            </div>
          </div>

          {/* 右サイド: プレビュー表示 */}
          <div className="col-span-12 lg:col-span-8">
            <div className="space-y-6">
              {/* プレビュー表示エリア */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 入力ファイルプレビュー */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {inputFile ? (
                    <FilePreview
                      file={inputFile}
                      title="入力ファイルプレビュー"
                      type="input"
                    />
                  ) : (
                    <div className="h-full min-h-[400px] flex items-center justify-center p-4 text-gray-500 text-center">
                      <p className="max-w-xs mx-auto">
                        ファイルを選択すると、ここにプレビューが表示されます
                      </p>
                    </div>
                  )}
                </div>
{/* 変換プレビュー表示 */}
{/* <div className="bg-white rounded-lg shadow-sm overflow-hidden">
  <ClipboardPreview
    imageData={previewImage}
    fileContent={null}
    isLoading={isLoading}
    error={previewError}
  />
</div> */}
                {/* 出力ファイルプレビューを入力ファイルプレビューの右横に移動 */}
                {outputFile && (
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <FilePreview
                      file={outputFile}
                      title="出力ファイルプレビュー"
                      type="output"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ローディングスピナー */}
      {isLoading && <LoadingSpinner />}
    </div>
  );
  //#endregion
};

export default NCConverter;